import { useRef, useState } from "react";
import { cloudinarySignature } from "@/lib/api";

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const VALID_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"];

/**
 * Drop-in replacement for an "image URL" input.
 * - Primary action: pick a file → uploads directly to Cloudinary (signed) → calls onChange(secure_url)
 * - Toggleable fallback: paste a URL if you already have one hosted
 * Backend exposes /api/admin/cloudinary/signature — see admin.py.
 */
export const ImageUploadField = ({
    value = "",
    onChange,
    folder = "naami/items",
    label = "Image",
    placeholder = "https://...",
    testId = "image-upload",
}) => {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [err, setErr] = useState("");
    const [showUrl, setShowUrl] = useState(false);

    const onPick = () => fileRef.current?.click();

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // reset so picking the same file twice re-fires
        if (!file) return;
        setErr("");
        if (!VALID_TYPES.includes(file.type)) {
            setErr("Please pick a JPG, PNG, WebP, GIF, or AVIF image.");
            return;
        }
        if (file.size > MAX_BYTES) {
            setErr(`Image is ${(file.size / 1024 / 1024).toFixed(1)}MB. Max is 10MB — try compressing first.`);
            return;
        }
        setUploading(true);
        setProgress(0);
        try {
            const sig = await cloudinarySignature(folder);
            const form = new FormData();
            form.append("file", file);
            form.append("api_key", sig.api_key);
            form.append("timestamp", sig.timestamp);
            form.append("signature", sig.signature);
            form.append("folder", sig.folder);

            // Use XHR for progress, fetch has no upload progress
            const xhr = new XMLHttpRequest();
            const url = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`;
            const result = await new Promise((resolve, reject) => {
                xhr.upload.onprogress = (ev) => {
                    if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
                };
                xhr.onload = () => {
                    try {
                        const data = JSON.parse(xhr.responseText);
                        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
                        else reject(new Error(data.error?.message || `HTTP ${xhr.status}`));
                    } catch {
                        reject(new Error("Bad response from Cloudinary"));
                    }
                };
                xhr.onerror = () => reject(new Error("Network error"));
                xhr.open("POST", url);
                xhr.send(form);
            });
            if (result?.secure_url) {
                onChange(result.secure_url);
            } else {
                throw new Error("Upload succeeded but no URL returned.");
            }
        } catch (e2) {
            setErr(e2.response?.data?.detail || e2.message || "Upload failed.");
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    return (
        <div data-testid={testId}>
            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">{label}</label>

            {/* Preview */}
            {value && (
                <div className="mb-3 flex items-center gap-3">
                    <div className="w-20 h-20 overflow-hidden bg-[var(--surface-elevated)] border border-[var(--border)] shrink-0 flex items-center justify-center">
                        <img src={value} alt="preview" className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">✓ Uploaded</p>
                        <p className="font-mono text-[10px] text-muted break-all line-clamp-2 mt-0.5">{value}</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-[#ff5246]"
                        data-testid={`${testId}-remove`}
                    >
                        ✕ Remove
                    </button>
                </div>
            )}

            {/* Primary upload action */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <button
                    type="button"
                    onClick={onPick}
                    disabled={uploading}
                    className="btn-secondary disabled:opacity-40"
                    data-testid={`${testId}-pick`}
                >
                    {uploading ? `Uploading… ${progress}%` : (value ? "↻ Replace image" : "📷 Upload from file")}
                </button>
                <button
                    type="button"
                    onClick={() => setShowUrl((s) => !s)}
                    className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-gold"
                    data-testid={`${testId}-toggle-url`}
                >
                    {showUrl ? "← Hide URL field" : "Use a URL instead →"}
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={onFile}
                    className="hidden"
                    data-testid={`${testId}-input`}
                />
            </div>

            {/* URL fallback */}
            {(showUrl || (value && !value.startsWith("https://res.cloudinary.com/"))) && (
                <input
                    type="url"
                    className="input"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={`${testId}-url-input`}
                />
            )}

            {err && (
                <p className="font-mono text-[11px] text-[#ff5246] mt-2" data-testid={`${testId}-error`}>{err}</p>
            )}
        </div>
    );
};

export default ImageUploadField;
